import type { CreateLeaveRequest, LeaveType } from '$lib/api/types';

export interface LeaveRequestFormInput {
	type: string;
	startDate: string;
	endDate: string;
	startTime: string;
	durationHours: number | string;
	reason: string;
	approvalLineId: number | string;
}

export type LeaveRequestFormResult =
	| { ok: true; request: CreateLeaveRequest }
	| { ok: false; message: string };

const LEAVE_TYPES = ['ANNUAL', 'HALF_AM', 'HALF_PM', 'HOURLY', 'SICK', 'SPECIAL'] as const;

export function isHourlyLeaveType(type: string): boolean {
	return type === 'HOURLY';
}

export function isHalfDayLeaveType(type: string): boolean {
	return type === 'HALF_AM' || type === 'HALF_PM';
}

export function validateLeaveRequestForm(input: LeaveRequestFormInput): LeaveRequestFormResult {
	if (!LEAVE_TYPES.includes(input.type as LeaveType)) {
		return { ok: false, message: '휴가 유형을 다시 선택해주세요.' };
	}

	const type = input.type as LeaveType;
	const approvalLineId = parsePositiveInteger(input.approvalLineId);
	if (approvalLineId === null) {
		return { ok: false, message: '결재선을 선택해주세요.' };
	}
	if (!input.startDate) {
		return { ok: false, message: '시작일을 선택해주세요.' };
	}
	if (!isValidIsoDate(input.startDate)) {
		return { ok: false, message: '시작일을 올바르게 선택해주세요.' };
	}

	const reason = optionalText(input.reason);

	if (isHourlyLeaveType(type)) {
		const durationHours = parsePositiveInteger(input.durationHours);
		if (!input.startTime) {
			return { ok: false, message: '시작 시각을 선택해주세요.' };
		}
		if (durationHours === null || durationHours < 1 || durationHours > 8) {
			return { ok: false, message: '시간 단위 휴가는 1~8시간으로 선택해주세요.' };
		}

		return {
			ok: true,
			request: {
				type,
				startDate: input.startDate,
				endDate: input.startDate,
				startTime: input.startTime,
				durationHours,
				approvalLineId,
				...(reason ? { reason } : {})
			}
		};
	}

	if (isHalfDayLeaveType(type)) {
		return {
			ok: true,
			request: {
				type,
				startDate: input.startDate,
				endDate: input.startDate,
				approvalLineId,
				...(reason ? { reason } : {})
			}
		};
	}

	if (!input.endDate) {
		return { ok: false, message: '종료일을 선택해주세요.' };
	}
	if (!isValidIsoDate(input.endDate)) {
		return { ok: false, message: '종료일을 올바르게 선택해주세요.' };
	}
	if (input.endDate < input.startDate) {
		return { ok: false, message: '종료일은 시작일 이후로 선택해주세요.' };
	}

	return {
		ok: true,
		request: {
			type,
			startDate: input.startDate,
			endDate: input.endDate,
			approvalLineId,
			...(reason ? { reason } : {})
		}
	};
}

function optionalText(value: string): string | undefined {
	const next = value.trim();
	return next ? next : undefined;
}

function parsePositiveInteger(value: number | string): number | null {
	const parsed = typeof value === 'number' ? value : Number(String(value).trim());
	return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

function isValidIsoDate(value: string): boolean {
	if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
	const [year, month, day] = value.split('-').map(Number);
	const date = new Date(year, month - 1, day);
	return (
		date.getFullYear() === year &&
		date.getMonth() === month - 1 &&
		date.getDate() === day
	);
}
